
import React, { useEffect, useState, useCallback, useRef, useContext } from 'react'; 
import { Alert } from '@mui/material';
import { useQuery, useSubscription } from '@apollo/client';
import { isEmpty } from 'lodash';
import AuthContext from '../../../auth/context/AuthContext';
import clientGraphql from '../../../../common/apollo-graphql';
import Referral from '../../../referral/models/Referral';
import GlobalLoader from '../../../common/components/loader/GlobalLoader';
import ReferralsTable from './ReferralsTable';

export default function DeclinedReferrals({
  query,
  uuid,
  subscription,
  onViewLogs,
  onView,
  columns
}) {
  const currentUser = useContext(AuthContext);

  // ---------------- STATE ----------------
  const [parsedData, setParsedData] = useState({ rows: [], count: 0 });
  const [searchFilters, setSearchFilters] = useState({
    type: 'Name',
    patientName: '',
    birthdate: '',
    filterFax: 'all',
  });
  const [closeError, setCloseError] = useState(false);
  const subscriptionsRef = useRef([]);
  const debounceRef = useRef(null);

  // ---------------- QUERY / SUBSCRIPTION ----------------
  const { loading, data, error, refetch } = useQuery(query, {
    client: clientGraphql,
    variables: {
      uuid,
      patientName: null,
      birthdate: null,
      isFax: null,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const { data: subscriptionData } = useSubscription(subscription, {
    variables: { clinic_id: currentUser?.clinics?.[0]?.id || null },
  });

  // ---------------- REFETCH HELPER ----------------
  const refetchData = useCallback(
    (filters) => {
      if (!refetch) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);

      debounceRef.current = setTimeout(() => {
        refetch({
          uuid,
          patientName: filters.patientName || null,
          birthdate: filters.birthdate || null,
          isFax:
            filters.filterFax === 'fax'
              ? true
              : filters.filterFax === 'none'
                ? false
                : null,
        }).then(({ data }) => {
          if (!isEmpty(data) && !isEmpty(data.rows)) {
            setParsedData({
              rows: data.rows.map((item) => ({
                ...item,
                referral: new Referral(item),
                actions: {
                  onViewNotes: onViewLogs,
                  onView: onView,
                },
              })),
              count: data.rows.length,
            });
          } else {
            setParsedData({ rows: [], count: 0 });
          }
        });
      }, 500);
    },
    [refetch, uuid, onView, onViewLogs]
  );

  // ---------------- FILTER HANDLERS ----------------
  const handleFilterChange = (e) => {
    const value = e.target.value;
    const newFilters = { ...searchFilters };
    if (searchFilters.type === 'Name') newFilters.patientName = value;
    else newFilters.birthdate = value;

    setSearchFilters(newFilters);
    refetchData(newFilters);
  };

  const handleTypeChange = (e) => {
    const newFilters = {
      type: e.target.value,
      patientName: '',
      birthdate: '',
      filterFax: searchFilters.filterFax,
    };
    setSearchFilters(newFilters);
    refetchData(newFilters);
  };

  // ---------------- SUBSCRIPTION EFFECT ----------------
  useEffect(() => {
    if (!isEmpty(subscriptionData)) {
      if (!isEmpty(subscriptionsRef.current) &&
        JSON.stringify(subscriptionData) !== JSON.stringify(subscriptionsRef.current)
      ) {
        refetchData(searchFilters);
      }
      subscriptionsRef.current = subscriptionData;
    }
  }, [subscriptionData, refetchData, searchFilters]);

  // ---------------- DATA EFFECT ----------------
  useEffect(() => {
    if (!isEmpty(data) && !isEmpty(data.rows)) {
      setParsedData({
        rows: data.rows.map((item) => ({
          ...item,
          referral: new Referral(item),
          actions: {
            onViewNotes: onViewLogs,
            onView: onView,
          },
        })),
        count: data.rows.length,
      });
    }
  }, [data, onViewLogs, onView]);

  if (loading) return <GlobalLoader />;

  return (
    <>
      {error && !closeError && (
        <Alert variant="filled" severity="error" onClose={() => setCloseError(true)}>
          We are sorry, but we are having difficulty with our system. We are unable to load data
          at the moment. Please reach out to us if you continue to experience this issue.
        </Alert>
      )}

      <ReferralsTable
        onFilterChanged={handleFilterChange}
        onChangeDropdown={handleTypeChange}
        loading={loading}
        refresh={() => refetchData(searchFilters)}
        data={!isEmpty(parsedData) ? parsedData : { rows: [], count: 0 }}
        columns={columns}
      />
    </>
  );
}
